/**
 * HW Featured Collections — Tab switching
 * Version: 1.3.0
 */

(function () {
  'use strict';

  const tabState = new WeakMap();
  const carouselObserverState = new WeakMap();

  const getTabButtons = (tabsEl) =>
    Array.from(tabsEl.querySelectorAll('[role="tab"][data-collection-handle]'));

  const getPanels = (sectionEl) =>
    Array.from(
      sectionEl.querySelectorAll(
        '[role="tabpanel"][data-hw-collection-content]',
      ),
    );

  /**
   * Resets a collection panel carousel to the first slide.
   *
   * Hidden panels initialise slideshow scroll while display:none, which can
   * leave scrollLeft at the end until the panel is shown. On iOS Safari the
   * visible panel can also land on the last card when slideshow scroll is
   * enabled after the in-viewport attribute is added.
   *
   * @param {HTMLElement} panel
   * @param {Object} [options]
   * @param {boolean} [options.force=false] - Reset even if the visitor has
   *   already driven this carousel, and hand control back to us. Only a
   *   deliberate tab switch should force; an incidental viewport re-entry must
   *   not yank a carousel the visitor has scrolled back to its first card.
   */
  const resetPanelCarousel = (panel, options) => {
    const slideshow = panel.querySelector('slideshow-component');
    if (!slideshow) {
      return;
    }

    const force = options?.force === true;

    // Shared with hw-carousel-grid-content.js, which sets this flag.
    if (force) {
      slideshow.removeAttribute('data-hw-user-driven');
    } else if (slideshow.hasAttribute('data-hw-user-driven')) {
      return;
    }

    const scroller = slideshow.querySelector('slideshow-slides');

    /*
     * The inline `scroll-behavior` override is required: slideshow-slides sets
     * `scroll-behavior: smooth`, so a plain `scrollLeft =` assignment animates
     * and a reset reads as though someone had clicked a nav arrow. Preferred
     * over `scrollTo({behavior: 'instant'})` because ScrollBehavior is a WebIDL
     * enum - Safari versions that predate `instant` reject the whole call.
     *
     * data-hw-self-driven tells hw-carousel-grid-content.js that the
     * `slideshow:select` this write provokes is ours, not the visitor's -
     * writing the offset directly bypasses slideshow.js's Scroller, so it
     * reports the move as `userInitiated`.
     */
    const jumpToStart = () => {
      if (!scroller) return;

      slideshow.setAttribute('data-hw-self-driven', '');

      const previousBehavior = scroller.style.scrollBehavior;

      scroller.style.scrollBehavior = 'auto';
      scroller.scrollLeft = 0;

      if (previousBehavior) {
        scroller.style.scrollBehavior = previousBehavior;
      } else {
        scroller.style.removeProperty('scroll-behavior');
      }
    };

    const releaseSelfDriven = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => slideshow.removeAttribute('data-hw-self-driven'));
      });
    };

    jumpToStart();

    const applyReset = () => {
      jumpToStart();

      if (typeof slideshow.select === 'function') {
        slideshow.select(0, undefined, { animate: false });
      }

      releaseSelfDriven();
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(applyReset);
    });
  };

  /**
   * Resets the active collection panel carousel on load and when its slideshow
   * enters the viewport (iOS Safari initial-position race).
   *
   * @param {HTMLElement} section
   */
  const wireActiveCarouselResets = (section) => {
    if (carouselObserverState.has(section)) {
      return;
    }

    const activePanel = section.querySelector(
      '[data-hw-collection-content][data-hw-collection-active]',
    );
    if (activePanel) {
      resetPanelCarousel(activePanel);
    }

    const observers = [];

    for (const slideshow of section.querySelectorAll('slideshow-component')) {
      const resetIfActive = () => {
        const panel = slideshow.closest('[data-hw-collection-content]');
        if (!panel || !panel.hasAttribute('data-hw-collection-active')) {
          return;
        }

        resetPanelCarousel(panel);
      };

      if (slideshow.hasAttribute('in-viewport')) {
        resetIfActive();
        continue;
      }

      /*
       * Disconnect after the first batch that concerns [in-viewport], whether
       * or not the attribute happens to be set when the callback runs. An
       * add+remove inside one batch used to leave this armed indefinitely,
       * so a later viewport re-entry could still fire the reset - long after
       * load, snapping a carousel the visitor had scrolled back to card one.
       */
      const observer = new MutationObserver((mutations) => {
        const sawInViewport = mutations.some(
          (mutation) => mutation.attributeName === 'in-viewport',
        );
        if (!sawInViewport) return;

        observer.disconnect();

        if (slideshow.hasAttribute('in-viewport')) {
          resetIfActive();
        }
      });

      observer.observe(slideshow, {
        attributes: true,
        attributeFilter: ['in-viewport'],
      });
      observers.push(observer);
    }

    carouselObserverState.set(section, observers);
  };

  const setActiveTab = (tabsEl, sectionEl, nextTab, shouldFocus) => {
    const tabs = getTabButtons(tabsEl);
    const panels = getPanels(sectionEl);
    if (!tabs.length || !panels.length || !tabs.includes(nextTab)) {
      return;
    }

    const targetHandle = nextTab.dataset.collectionHandle || '';
    let hasActivePanel = false;

    for (const tab of tabs) {
      const isActive = tab === nextTab;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const panel of panels) {
      const isActive =
        !hasActivePanel && panel.dataset.collectionHandle === targetHandle;

      if (isActive) {
        panel.setAttribute('data-hw-collection-active', '');
        panel.removeAttribute('hidden');
        resetPanelCarousel(panel, { force: true });
        hasActivePanel = true;
        continue;
      }

      panel.removeAttribute('data-hw-collection-active');
      panel.setAttribute('hidden', '');
    }

    if (shouldFocus) {
      nextTab.focus();
    }

    // Tell the shared nav arrows / progress bar (rendered once, outside any
    // single collection panel) to re-resolve and re-wire to whichever
    // panel's slideshow-component is now visible. Without this they stay
    // latched onto the first collection's carousel.
    sectionEl.dispatchEvent(
      new CustomEvent('hw-tabs:activate', { bubbles: true }),
    );
  };

  function initFeaturedCollections(tabsEl) {
    if (tabState.has(tabsEl)) return;

    const section = tabsEl.closest('.section-carousel-grid');
    if (!section) return;

    const clickHandler = (event) => {
      const tab = event.target.closest('[role="tab"][data-collection-handle]');
      if (!tab || !tabsEl.contains(tab)) return;
      setActiveTab(tabsEl, section, tab, false);
    };

    const keydownHandler = (event) => {
      const tab = event.target.closest('[role="tab"][data-collection-handle]');
      if (!tab || !tabsEl.contains(tab)) return;

      const tabs = getTabButtons(tabsEl);
      const currentIndex = tabs.indexOf(tab);
      if (currentIndex < 0) return;

      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % tabs.length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = tabs.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      setActiveTab(tabsEl, section, tabs[nextIndex], true);
    };

    tabsEl.addEventListener('click', clickHandler);
    tabsEl.addEventListener('keydown', keydownHandler);
    tabState.set(tabsEl, { clickHandler, keydownHandler });
    wireActiveCarouselResets(section);
  }

  function destroyFeaturedCollections(rootEl) {
    const tabsLists = rootEl.querySelectorAll('[data-hw-collection-tabs]');
    for (const tabsEl of tabsLists) {
      const state = tabState.get(tabsEl);
      if (!state) continue;

      tabsEl.removeEventListener('click', state.clickHandler);
      tabsEl.removeEventListener('keydown', state.keydownHandler);
      tabState.delete(tabsEl);
    }

    const sections = rootEl.classList?.contains('section-carousel-grid')
      ? [rootEl]
      : Array.from(rootEl.querySelectorAll('.section-carousel-grid'));

    for (const section of sections) {
      const observers = carouselObserverState.get(section);
      if (!observers) continue;

      for (const observer of observers) {
        observer.disconnect();
      }
      carouselObserverState.delete(section);
    }
  }

  function init() {
    document.querySelectorAll('[data-hw-collection-tabs]').forEach(initFeaturedCollections);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor live updates
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-hw-collection-tabs]').forEach(initFeaturedCollections);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    destroyFeaturedCollections(e.target);
  });
})();
